import React, { useState, useEffect, useMemo } from 'react';
import { Search, Download, GraduationCap, Calendar, Clock, MapPin, Hash, Eye, X, BookOpen, User } from 'lucide-react';
import * as XLSX from 'xlsx';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000/api/v1';

export default function JefaturaConsultasAsignaturas({ readOnly = false }) {
    const [reporte, setReporte] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [selectedStudent, setSelectedStudent] = useState(null);
    const currentYear = new Date().getFullYear();
    const [selectedGestion, setSelectedGestion] = useState(currentYear);

    useEffect(() => {
        loadReporte();
    }, [selectedGestion]);

    const loadReporte = async () => {
        try {
            setLoading(true);
            // Ya no mandamos &q= al servidor, filtramos client-side
            const response = await fetch(`${API_URL}/jefatura/reporte-inscripciones-detalle?gestion=${selectedGestion}`);
            const data = await response.json();
            if (data.reporte) {
                setReporte(data.reporte);
            }
        } catch (error) {
            console.error('Error cargando reporte:', error);
        } finally {
            setLoading(false);
        }
    };

    // Agrupar + deduplicar asignaturas
    const reporteAgrupado = useMemo(() => {
        const grouped = reporte.reduce((acc, curr) => {
            const studentKey = curr.ci || curr.codigo;
            if (!acc[studentKey]) {
                acc[studentKey] = {
                    codigo: curr.codigo,
                    nombres: curr.nombres,
                    apellidos: curr.apellidos,
                    ci: curr.ci,
                    carrera: curr.carrera,
                    mencion: curr.mencion,
                    modalidad_admision: curr.modalidad_admision,
                    asignaturas: []
                };
            }
            if (curr.asignatura) {
                // Deduplicar por código + paralelo
                const asigKey = `${curr.codigo_asignatura}-${curr.paralelo}`;
                const yaExiste = acc[studentKey].asignaturas.some(
                    a => `${a.codigo_asignatura}-${a.paralelo}` === asigKey
                );
                if (!yaExiste) {
                    acc[studentKey].asignaturas.push({
                        codigo_asignatura: curr.codigo_asignatura,
                        asignatura: curr.asignatura,
                        paralelo: curr.paralelo,
                        sala: curr.sala,
                        dia: curr.dia,
                        hora_inicio: curr.hora_inicio,
                        hora_fin: curr.hora_fin
                    });
                }
            }
            return acc;
        }, {});
        return Object.values(grouped);
    }, [reporte]);

    // Filtro client-side por searchTerm
    const reporteFiltrado = useMemo(() => {
        if (!searchTerm.trim()) return reporteAgrupado;
        const term = searchTerm.toLowerCase();
        return reporteAgrupado.filter(r =>
            r.nombres?.toLowerCase().includes(term) ||
            r.apellidos?.toLowerCase().includes(term) ||
            r.ci?.toLowerCase().includes(term) ||
            r.codigo?.toLowerCase().includes(term)
        );
    }, [reporteAgrupado, searchTerm]);

    const openDetails = (student) => {
        setSelectedStudent(student);
        setShowModal(true);
    };

    const exportToExcel = () => {
        const dataToExport = reporte.map((r, index) => ({
            'No.': index + 1,
            'Código': r.codigo,
            'Nombre': r.nombres,
            'Apellidos': r.apellidos,
            'CI': r.ci,
            'Carrera': r.carrera,
            'Mención': r.mencion || '-',
            'Modalidad de Admisión': r.modalidad_admision,
            'Código Asignatura': r.codigo_asignatura,
            'Asignatura': r.asignatura,
            'Paralelo': r.paralelo,
            'Sala': r.sala,
            'Día': r.dia,
            'Horario Inicio': r.hora_inicio,
            'Horario Fin': r.hora_fin
        }));

        const ws = XLSX.utils.json_to_sheet(dataToExport);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Matriculación Detallada");
        XLSX.writeFile(wb, `Reporte_Matriculacion_${selectedGestion}.xlsx`);
    };

    return (
        <div className="p-6">
            <div className="mb-6">
                <h1 className="text-xl font-bold text-gray-800">
                    {!readOnly ? 'Jefatura de Carrera' : 'Dirección Académica'} &gt; Consultas &gt; Asignaturas Inscritas
                </h1>
            </div>

            <div className="mb-6 bg-white p-4 rounded-xl shadow-sm border border-gray-100">
                <div className="flex flex-wrap gap-4 items-end">
                    <div className="flex-grow flex items-end gap-2">
                        <div className="relative flex-grow">
                            <label className="block text-xs font-semibold text-gray-500 mb-1 ml-1">BUSCAR</label>
                            <div className="relative">
                                <Search className="absolute left-3 top-2.5 text-gray-400" size={16} />
                                <input
                                    type="text"
                                    placeholder="Buscar por nombre, apellido, CI o código..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-blue-100 hover:border-blue-500"
                                />
                            </div>
                        </div>

                        <button
                            onClick={exportToExcel}
                            className="p-2 bg-green-600 text-white rounded-full hover:bg-green-700 transition shadow-sm"
                            title="Exportar Detalle a Excel"
                        >
                            <Download size={20} />
                        </button>

                        {/* <div className="flex items-center gap-2 px-6 py-2 bg-blue-900 text-white rounded-2xl shadow-lg shadow-blue-900/20 font-bold">
                            <GraduationCap size={20} />
                            Matriculación (Lic.)
                        </div> */}
                    </div>
                </div>
            </div>

            {/* Contador de resultados */}
            {searchTerm && (
                <p className="text-xs text-gray-500 mb-2 ml-1">
                    {reporteFiltrado.length} resultado(s) para "<span className="font-semibold">{searchTerm}</span>"
                </p>
            )}

            <div className="bg-white rounded-lg shadow overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="bg-gray-100">
                            <tr>
                                <th className="px-3 py-3 text-center font-semibold border">No.</th>
                                <th className="px-3 py-3 text-center font-semibold border">Código</th>
                                <th className="px-3 py-3 text-center font-semibold border">Estudiante</th>
                                <th className="px-3 py-3 text-center font-semibold border">Información académica</th>
                                <th className="px-3 py-3 text-center font-semibold border">Asignaturas</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr>
                                    <td colSpan="5" className="px-6 py-20 text-center">
                                        <div className="flex flex-col items-center gap-2">
                                            <div className="w-8 h-8 border-2 border-gray-200 border-t-blue-600 rounded-full animate-spin"></div>
                                            <p className="text-xs text-gray-500 font-medium tracking-tight">Cargando...</p>
                                        </div>
                                    </td>
                                </tr>
                            ) : reporteFiltrado.length === 0 ? (
                                <tr>
                                    <td colSpan="5" className="px-6 py-20 text-center">
                                        <p className="text-sm text-gray-400">
                                            {searchTerm ? 'No se encontraron resultados para tu búsqueda.' : 'No se encontraron registros.'}
                                        </p>
                                    </td>
                                </tr>
                            ) : (
                                reporteFiltrado.map((r, idx) => (
                                    <tr key={idx} className="hover:bg-gray-50">
                                        <td className="px-3 py-2 text-center border">{idx + 1}</td>
                                        <td className="px-3 py-2 text-center border font-medium">{r.codigo}</td>
                                        <td className="px-3 py-2 border">
                                            <div className="flex flex-col">
                                                <span className="text-xs font-bold text-gray-900 uppercase">{r.nombres}</span>
                                                <span className="text-[11px] text-gray-600">{r.apellidos}</span>
                                                <span className="text-[10px] text-gray-400 font-medium">CI: {r.ci}</span>
                                            </div>
                                        </td>
                                        <td className="px-3 py-2 border">
                                            <div className="flex flex-col sm:flex-row gap-5">
                                                <div className="flex flex-col gap-0.5">
                                                    <span className="text-[10px] font-bold text-blue-700 bg-blue-50 px-2 py-0.5 rounded w-fit capitalize">
                                                        {r.carrera}
                                                    </span>
                                                    {r.mencion && (
                                                        <span className="text-[9px] text-gray-500 pl-1 italic">{r.mencion}</span>
                                                    )}
                                                </div>
                                                <div className="flex flex-col">
                                                    <span className={`text-[9px] font-bold px-2 py-0.5 rounded-full w-fit ${r.modalidad_admision?.includes('CPA') ? 'bg-green-100 text-green-700' :
                                                        r.modalidad_admision?.includes('PAA') ? 'bg-blue-100 text-blue-700' :
                                                            r.modalidad_admision?.includes('AE') ? 'bg-red-100 text-red-700' :
                                                                'bg-gray-100 text-gray-600'
                                                        }`}>
                                                        {r.modalidad_admision || 'ADMISIÓN NORMAL'}
                                                    </span>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-3 py-2 border text-center">
                                            <button
                                                onClick={() => openDetails(r)}
                                                className="p-2 text-blue-600 hover:bg-blue-100 rounded-full transition-colors relative group"
                                                title="Ver asignaturas inscritas"
                                            >
                                                <Eye size={20} />
                                                <span className="absolute -top-1 -right-1 bg-blue-600 text-white text-[9px] w-4 h-4 rounded-full flex items-center justify-center font-black shadow-sm">
                                                    {r.asignaturas.length}
                                                </span>
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Modal */}
            {showModal && selectedStudent && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
                        <div className="flex justify-between items-center px-6 py-4 border-b sticky top-0 bg-white z-10">
                            <div>
                                <h2 className="text-xl font-bold text-blue-900">Asignaturas y Horarios Inscritos ({selectedStudent.asignaturas.length})</h2>
                                <p className="text-sm text-gray-600">{selectedStudent.nombres} {selectedStudent.apellidos} - {selectedStudent.codigo}</p>
                            </div>
                            <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600">
                                <X size={24} />
                            </button>
                        </div>
                        {/* <div className="p-6 bg-blue-950 text-white flex items-center justify-between">
                            <div className="flex items-center gap-5">
                                <div className="p-3 bg-white/10 rounded-2xl">
                                    <User size={28} className="text-blue-300" />
                                </div>
                                <div>
                                    <h2 className="text-xl font-black uppercase tracking-tight">{selectedStudent.nombres} {selectedStudent.apellidos}</h2>
                                    <div className="flex gap-3 text-sm opacity-80 font-bold mt-0.5">
                                        <span className="flex items-center gap-1"><Hash size={14} /> {selectedStudent.codigo}</span>
                                        <span>|</span>
                                        <span>CI: {selectedStudent.ci}</span>
                                    </div>
                                </div>
                            </div>
                            <button onClick={() => setShowModal(false)} className="p-2 hover:bg-white/10 rounded-full transition-colors">
                                <X size={24} />
                            </button>
                        </div> */}

                        <div className="p-8 overflow-y-auto">


                            <div className="bg-white rounded-lg shadow overflow-hidden">
                                <table className="w-full text-sm">
                                    <thead className="bg-gray-100">
                                        <tr>
                                            <th className="px-3 py-3 text-center font-semibold border">Código</th>
                                            <th className="px-3 py-3 text-center font-semibold border">Asignatura</th>
                                            <th className="px-3 py-3 text-center font-semibold border text-center">Paralelo</th>
                                            <th className="px-3 py-3 text-center font-semibold border">Día</th>
                                            <th className="px-3 py-3 text-center font-semibold border text-center">Sala</th>
                                            <th className="px-3 py-3 text-center font-semibold border text-center">Horario</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-100">
                                        {selectedStudent.asignaturas.map((asig, index) => (
                                            <tr key={index} className="hover:bg-gray-50">
                                                <td className="px-3 py-2 text-center border">{asig.codigo_asignatura}</td>
                                                <td className="px-3 py-2 border">
                                                    <span className="text-xs font-bold text-gray-800 uppercase ">{asig.asignatura}</span>
                                                </td>
                                                <td className="px-3 py-2 border text-center">
                                                    <span>
                                                        {asig.paralelo}
                                                    </span>
                                                </td>
                                                <td className="px-3 py-2 border">
                                                    <div className="flex items-center gap-2 text-[10px] font-black uppercase">
                                                        <Calendar size={13} className="text-gray-400" />
                                                        {asig.dia}
                                                    </div>
                                                </td>
                                                <td className="px-3 py-2 border text-center">
                                                    <div className="flex items-center justify-center gap-1.5 text-[10px] font-black text-gray-800">
                                                        <MapPin size={12} className="text-red-500" />
                                                        {asig.sala}
                                                    </div>
                                                </td>
                                                <td className="px-3 py-2 border text-center">
                                                    <div className="flex items-center justify-center gap-1.5 text-[10px] text-gray-700  py-1">
                                                        <Clock size={12} className="text-blue-600" />
                                                        {asig.hora_inicio} - {asig.hora_fin}
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        <div className="p-6 border-t bg-gray-50 flex justify-end">
                            <button
                                onClick={() => setShowModal(false)}
                                className="px-6 py-2 bg-blue-900 text-white rounded-md hover:bg-blue-800"
                            >
                                Cerrar Ventana
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}